import { useCallback } from 'react'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { PaymentProvider } from '@model/PaymentProviderDto'
import type { Wallet } from '@model/WalletDepositFormValues'
import { WalletDto } from '@model/WalletDto'
import { WalletPaymentProvider } from '@model/WalletPaymentProvider'
import { WalletPaymentProviderParameterMessage } from '@model/WalletPaymentProviderParameters'

import {
  GetDepositCurrencyLimitsValue,
  useGetDepositCurrencyLimits,
} from '../WalletDepositForm/hooks/useGetDepositCurrencyLimits'

export const usePaymentProviderFactory = (): {
  createPaymentProvider: (
    walletPaymentProvider: WalletPaymentProvider,
    wallet: Wallet | WalletDto
  ) => Promise<PaymentProvider>
} => {
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)

  const { getDepositCurrencyLimits } = useGetDepositCurrencyLimits()

  const createPaymentProvider = useCallback(
    async (
      walletPaymentProvider: WalletPaymentProvider,
      wallet: Wallet | WalletDto
    ): Promise<PaymentProvider> => {
      const walletPaymentProviderParameters = await apiClient.getWalletDepositMethodParameters(
        locale,
        wallet.id || '',
        walletPaymentProvider.method.id
      )

      const paymentProvider: PaymentProvider = {
        id: walletPaymentProvider.method.id,
        name: walletPaymentProvider.name,
        description: walletPaymentProvider.description,
        providerCategory: walletPaymentProvider.providerCategory,
        currency: {
          id: walletPaymentProvider.currency.id,
          ...walletPaymentProvider,
        },
        parameters: walletPaymentProviderParameters,
        method_name: walletPaymentProvider.method_name,
      }

      const depositCurrencyLimits = await getDepositCurrencyLimits(wallet, paymentProvider)

      return {
        ...paymentProvider,
        parameters: {
          ...walletPaymentProviderParameters,
          messages: walletPaymentProviderParameters.messages.map((x) =>
            formatWalletWarningMessage(x, paymentProvider, depositCurrencyLimits)
          ),
        },
      }
    },
    [locale, apiClient, getDepositCurrencyLimits]
  )

  return { createPaymentProvider }
}

const formatWalletWarningMessage = (
  message: WalletPaymentProviderParameterMessage,
  paymentProvider: PaymentProvider,
  currencyLimit?: GetDepositCurrencyLimitsValue
): WalletPaymentProviderParameterMessage => {
  const minAmount = currencyLimit?.minAmount?.toString() || '0'
  const maxAmount = currencyLimit?.maxAmount?.toString() || '0'
  const currency = paymentProvider.currency.id || ''

  return {
    ...message,
    list: message.list.map((x) =>
      x
        .replace(/{minAmount}/g, minAmount)
        .replace(/{maxAmount}/g, maxAmount)
        .replace(/{paymentProviderCurrency}/g, currency)
    ),
  }
}
